import React, { useState } from 'react';
import { FaUpload, FaTimes } from 'react-icons/fa';
import { uploadSignedInit, uploadSignedComplete } from '../services/DocumentService';
import axios from 'axios';
import { personalakteStyles as ps, colors, shadows } from '../styles/personalakteStyles';

interface UploadSignedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  documentName: string;
  employeeId: number;
  documentId: number;
}

export function UploadSignedModal({ isOpen, onClose, onSuccess, documentName, employeeId, documentId }: UploadSignedModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [signedAt, setSignedAt] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [error, setError] = useState<string>('');


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setError('Please select a PDF file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file');
      return;
    }
    if (!signedAt) {
      setError('Please select the signing date');
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress('Initializing upload...');

    try {
      const initResponse = await uploadSignedInit(employeeId, documentId);

      setUploadProgress('Uploading to storage...');
      await axios.put(initResponse.upload_url, file, {
        headers: {
          'Content-Type': 'application/pdf',
        },
      });

      setUploadProgress('Finalizing upload...');
      await uploadSignedComplete(employeeId, documentId, initResponse.final_url_hint);

      setUploadProgress('Upload complete!');
      onSuccess();
      onClose();
      setFile(null);
      setSignedAt('');
      setUploadProgress('');
    } catch (err: any) {
      console.error('Upload failed:', err);
      const errorMsg = err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Upload failed';
      setError(errorMsg);
      setUploadProgress('');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setSignedAt('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: colors.bgCard,
          borderRadius: 12,
          maxWidth: '500px',
          width: '90%',
          boxShadow: shadows.elevated,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: 24, borderBottom: `1px solid ${colors.border}` }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.navy }}>
            Upload Signed Document
          </h3>
          <p style={{ margin: '8px 0 0 0', fontSize: 14, color: colors.textMuted }}>
            {documentName}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: 24 }}>
            {error && (
              <div
                style={{
                  padding: 12,
                  backgroundColor: colors.dangerLight,
                  color: colors.danger,
                  borderRadius: 8,
                  marginBottom: 20,
                  fontSize: 14,
                  border: `1px solid ${colors.danger}`,
                }}
              >
                {error}
              </div>
            )}

            {uploadProgress && (
              <div
                style={{
                  padding: 12,
                  backgroundColor: colors.infoLight,
                  color: colors.info,
                  borderRadius: 8,
                  marginBottom: 20,
                  fontSize: 14,
                  border: `1px solid ${colors.info}`,
                  textAlign: 'center',
                  fontWeight: 500,
                }}
              >
                {uploadProgress}
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label style={ps.labelStyle}>Signed PDF File *</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                disabled={uploading}
                style={{
                  ...ps.inputStyle,
                  cursor: uploading ? 'not-allowed' : 'pointer',
                }}
              />
              {file && (
                <div style={{ marginTop: 8, fontSize: 13, color: colors.textMuted }}>
                  Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </div>
              )}
            </div>

            <div>
              <label style={ps.labelStyle}>Signed At *</label>
              <input
                type="date"
                value={signedAt}
                onChange={(e) => setSignedAt(e.target.value)}
                disabled={uploading}
                style={ps.inputStyle}
                max={new Date().toISOString().split('T')[0]}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: colors.textMuted }}>
                Date when the document was signed
              </div>
            </div>
          </div>

          <div
            style={{
              padding: 24,
              borderTop: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              style={{
                ...ps.buttonSecondary,
                cursor: uploading ? 'not-allowed' : 'pointer',
              }}
            >
              <FaTimes size={14} />
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !file || !signedAt}
              style={uploading || !file || !signedAt ? ps.buttonDisabled : ps.buttonPrimary}
            >
              <FaUpload size={14} />
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
